import { useCallback } from 'react';
import { create } from 'zustand';
import { assignShift, editShiftSeries, EditShiftSeriesRequestData } from '@libs/api/shifts';
import { getShiftRotation } from '@libs/api/shifts/getShift';
import { useNotification } from '@libs/snackbar';
import { ShiftRecurrent } from '@libs/models/shifts/series';
import { parseError } from '@libs/api/errors';
import { Analytics } from '@libs/analytics';

type State = {
  loading: boolean;
  drawerOpen: boolean;
  seriesId: string;
  initiated: boolean;
  data: ShiftRecurrent | null;
  load: (id: string) => Promise<void>;
  openShiftRotationDrawer: (id: string) => Promise<void>;
  closeShiftRotationDrawer: () => void;
  saveRotation: (id: string, shift: EditShiftSeriesRequestData) => Promise<void>;
  assignShift: (shiftId: string, userId: string) => Promise<void>;
  assignSubmitting: boolean;
};

export const useRotationSeriesEditStore = create<State>()((set, get) => ({
  loading: false,
  drawerOpen: false,
  initiated: false,
  seriesId: '',
  data: null,
  assignSubmitting: false,
  load: async (id) => {
    set({ loading: true });
    try {
      const resp = await getShiftRotation(id);
      set({
        loading: false,
        data: {
          ...resp.data,
          id: resp.data.id,
          externalId: resp.data.externalId,
          department: resp.data.department.id,
          subDepartment: resp.data.subDepartment?.id ?? null,
          speciality: resp.data.speciality?.id ?? null,
          staffingType: resp.data.staffingType,
          qualifications: resp.data.staffNurseQualificationFilters,
          assignees: resp.data.staff.map((s) => ({
            id: s.id,
            name: [s.firstName, s.lastName].filter(Boolean).join(' '),
            avatar: s.imageURL,
          })),
          canShare: !!resp.data.canShare,
          events: resp.data.events.map((event) => ({
            id: event.id,
            startDate: event.startDate.dateTime,
            endDate: event.endDate.dateTime,
            timeZone: event.startDate.timeZone,
            status: event.status,
            assignee: event.staff
              ? {
                  id: event.staff.id,
                  name: [event.staff.firstName, event.staff.lastName].filter(Boolean).join(' '),
                  avatar: event.staff.imageURL,
                }
              : null,
            qualifications: resp.data.staffNurseQualificationFilters,
          })),
          isPublished: resp.data.isPublished,
          isShared: resp.data.isShared,
          isTerminated: resp.data.isTerminated,
          isArchived: resp.data.isArchive,
          locationId: resp.data.locationId,
          tags: resp.data.tags || [],
          notes: [
            {
              id: 'shiftNote',
              title: 'Note',
              value: resp.data.note || '',
            },
          ],
          isUrgent: !!resp.data.isUrgent,
          allowedMultipleAssignees: !!resp.data.canHasMultipleAssignee,
        },
      });
    } finally {
      set({ loading: false, initiated: true });
    }
  },
  openShiftRotationDrawer: async (id) => {
    set({ seriesId: id, drawerOpen: true });
    await get().load(id);
  },
  closeShiftRotationDrawer: () => {
    set({ drawerOpen: false, data: null, seriesId: '' });
  },
  saveRotation: async (id, series) => {
    await editShiftSeries(id, series);
  },
  assignShift: async (shiftId: string, userId: string) => {
    set({ assignSubmitting: true });
    try {
      await assignShift(shiftId, userId);
      Analytics.track('Shift: Assigned', {
        shift_id: shiftId,
      });
    } finally {
      set({ assignSubmitting: false });
    }
  },
}));

export function useRotationDrawerState() {
  const { showErrorIntl } = useNotification();
  const open = useRotationSeriesEditStore((state) => state.openShiftRotationDrawer);
  const close = useRotationSeriesEditStore((state) => state.closeShiftRotationDrawer);

  return [
    useCallback(
      async (id: string) => {
        try {
          await open(id);
        } catch (error) {
          showErrorIntl(parseError(error).message);
          throw error;
        }
      },
      [open, showErrorIntl],
    ),
    close,
  ];
}
